import { styled } from '@mui/material/styles';
import { AppBar as MuiAppBar, IconButton, Toolbar, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useCallback, useState } from 'react';
import AppSwitch from '../Buttons/AppSwitch';
import LanguageSwitch from '../Buttons/LanguageSwitch';
import AuthenticationButton from '../Auth/AuthenticationButton';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { envName } from '../../config';
import { IUser } from '../../types/User.interface';
import { useTitleContext } from '../../store/Page/TitleContext';
import SideDrawer from './SideDrawer';

const isBeta = envName === 'beta';
const DRAWER_WIDTH = 240;

type MuiAppBarProps = {
    open: boolean;
};

const StyledAppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<MuiAppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: DRAWER_WIDTH,
        width: `calc(100% - ${DRAWER_WIDTH}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

interface AppBarsProps {
    user: null | IUser;
    position?: 'fixed' | 'absolute' | 'sticky' | 'relative' | 'static';
}

function AppBars({ user, position }: AppBarsProps) {
    const [selectedMenuItem, setSelectedMenuItem] = useState('');
    const [open, setOpen] = useLocalStorage<boolean>('sidebarOpen', false);
    const { title } = useTitleContext();

    const handleDrawerOpen = useCallback(() => {
        setOpen(true);
    }, [setOpen]);

    return (
        <>
            <StyledAppBar position={position} open={!!user?.isCallmartUser && open}>
                <Toolbar>
                    {user?.isCallmartUser && (
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={{
                                marginRight: '36px',
                                ...(open && { display: 'none' }),
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {title || selectedMenuItem}
                    </Typography>
                    {isBeta && (
                        <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'red', fontWeight: '700' }}>
                            BETA
                        </Typography>
                    )}
                    <AppSwitch />
                    <LanguageSwitch />
                    <AuthenticationButton />
                </Toolbar>
            </StyledAppBar>

            {user?.isCallmartUser && (
                <SideDrawer open={open} setOpen={setOpen} user={user} onSelectedMenuItemChange={setSelectedMenuItem} />
            )}
        </>
    );
}

export default AppBars;
